import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import * as XLSX from 'xlsx';
import api, { uploadUrl } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import Icon from '../components/Icon';
import './DepensesPerso.css';

const ONGLETS = [
  { id: 'overview', label: "Vue d'ensemble" },
  { id: 'km', label: 'Kilométrage' },
  { id: 'depenses', label: 'Dépenses' },
  { id: 'types', label: 'Types' },
  { id: 'resume', label: 'Résumé annuel' },
];
const DESTINATIONS = ['Costco', 'Lieu de production', 'Livraison client', "Réunion d'affaires", 'Autre'];
const TYPES_PROD = ['', 'Ribs + Pulled Pork', 'Saucisses', 'Brisket', 'Sauce BBQ', 'Saumon Fumé', 'Achat matières', 'Livraison commande', 'Autre'];
const CATEGORIES = ['bureau', 'kilometrage', 'abonnement', 'electricite', 'formation', 'telephone', 'repas', 'interet', 'autre'];
const CATEGORY_LABELS = { bureau: 'Bureau', kilometrage: 'Kilométrage', abonnement: 'Abonnements', electricite: 'Électricité', formation: 'Formation', telephone: 'Téléphone', repas: 'Repas', interet: 'Intérêts', autre: 'Autre' };
const MONTHS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const CURRENT_YEAR = new Date().getFullYear();

function money(v) { return `${Number(v || 0).toFixed(2)}$`; }
function num(v, d = 2) { return Number(v || 0).toFixed(d); }
function shareOf(item) { if ((item.repartition_lcu || 0) > 0 && (item.repartition_ela || 0) > 0) return 'Partagée'; if ((item.repartition_lcu || 0) > 0) return 'LCU'; if ((item.repartition_ela || 0) > 0) return 'ELA'; return 'Aucune'; }
function monthOf(item) { return item.mois_effectif || item.mois || Number(String(item.date_depense || '').slice(5, 7)) || null; }
function normalizePctAffaires(value) {
  const pct = Number(value || 0);
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return pct > 1 ? pct / 100 : pct;
}
function pctAffairesInputValue(value) { return normalizePctAffaires(value); }
function pctAffairesDisplay(value) { return `${num(normalizePctAffaires(value) * 100, 2)}%`; }
function calcDeduction({ montant_brut, pct_affaires, portion_foyer, repartition_lcu, repartition_ela }) {
  const total = Number(montant_brut || 0) * normalizePctAffaires(pct_affaires) * Number(portion_foyer || 1);
  return {
    deduction_totale: Math.round(total * 100) / 100,
    deduction_lcu: Math.round(total * (Number(repartition_lcu || 0) / 100) * 100) / 100,
    deduction_ela: Math.round(total * (Number(repartition_ela || 0) / 100) * 100) / 100,
  };
}

function ModalShell({ title, onClose, children, wide = false }) {
  // Portalé dans document.body : le modal doit échapper aux ancêtres avec transform
  // (ex: .rise-in sur .dp-section-card laisse un translateY(0) qui crée un nouveau
  // containing block et piège position:fixed dans les bornes de la card).
  return createPortal(
    <div className="dp-modal-overlay" onClick={onClose}>
      <div className={`dp-modal-box${wide ? ' dp-modal-wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="dp-modal-header">
          <h3 className="dp-modal-title">{title}</h3>
          <button className="dp-modal-close" onClick={onClose} aria-label="Fermer">
            <Icon name="fermer" size={16} />
          </button>
        </div>
        <div className="dp-modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

function ModalDeplacement({ associe, config, existing, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date_dep: existing?.date_dep || today,
    destination: existing?.destination || 'Lieu de production',
    type_prod: existing?.type_prod || '',
    description: existing?.description || '',
    distance_km: existing ? (existing.aller_retour ? existing.distance_km / 2 : existing.distance_km) : 38,
    aller_retour: existing ? existing.aller_retour : 1,
    notes: existing?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const taux = config?.taux_km || 0.72;
  const distanceReelle = (Number(form.distance_km) || 0) * (form.aller_retour ? 2 : 1);
  const deduction = distanceReelle * taux;
  const patch = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { associe, date_dep: form.date_dep, destination: form.destination, type_prod: form.type_prod || null, description: form.description || null, distance_km: Number(form.distance_km || 0), aller_retour: form.aller_retour ? 1 : 0, notes: form.notes || null };
      if (existing) await api.put(`/depenses-perso/deplacements/${existing.id}`, payload);
      else await api.post('/depenses-perso/deplacements', payload);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={`${existing ? 'Modifier' : 'Nouveau'} déplacement — ${associe}`} onClose={onClose}>
      <form className="dp-form" onSubmit={submit}>
        <div className="dp-form-grid two-cols">
          <div className="dp-field"><label>Date</label><DateInput value={form.date_dep} onChange={(v) => patch('date_dep', v)} required /></div>
          <div className="dp-field"><label>Destination</label><select value={form.destination} onChange={(e) => patch('destination', e.target.value)}>{DESTINATIONS.map((d) => <option key={d} value={d}>{d}</option>)}</select></div>
          <div className="dp-field"><label>Type production</label><select value={form.type_prod} onChange={(e) => patch('type_prod', e.target.value)}>{TYPES_PROD.map((t) => <option key={t} value={t}>{t || 'Aucun'}</option>)}</select></div>
          <div className="dp-field"><label>Distance aller simple (km)</label><input type="number" min="0" step="0.1" value={form.distance_km} onChange={(e) => patch('distance_km', e.target.value)} required /></div>
        </div>
        <div className="dp-field">
          <label>Sens</label>
          <div className="dp-radio-group">
            <label className="dp-radio-label"><input type="radio" checked={!!form.aller_retour} onChange={() => patch('aller_retour', 1)} />Aller-retour</label>
            <label className="dp-radio-label"><input type="radio" checked={!form.aller_retour} onChange={() => patch('aller_retour', 0)} />Sens unique</label>
          </div>
        </div>
        <div className="dp-formula-box">
          <span>Distance réelle : <strong>{num(distanceReelle, 1)} km</strong></span>
          <span>Déduction : <strong className="gold">{money(deduction)}</strong></span>
        </div>
        <div className="dp-field"><label>Description</label><input type="text" value={form.description} onChange={(e) => patch('description', e.target.value)} /></div>
        <div className="dp-field"><label>Notes</label><textarea rows="3" value={form.notes} onChange={(e) => patch('notes', e.target.value)} /></div>
        <div className="dp-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalTypeDepense({ existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    nom: existing?.nom || '',
    categorie: existing?.categorie || 'abonnement',
    description: existing?.description || '',
    pct_affaires: pctAffairesInputValue(existing?.pct_affaires ?? 1),
    repartition_lcu: existing?.repartition_lcu ?? 100,
    repartition_ela: existing?.repartition_ela ?? 0,
    portion_foyer: existing?.portion_foyer ?? 1,
    recurrent: existing?.recurrent ? 1 : 0,
    montant_defaut: existing?.montant_defaut ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const patch = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const repartitionTotal = Number(form.repartition_lcu || 0) + Number(form.repartition_ela || 0);
  const example = calcDeduction({ montant_brut: 100, pct_affaires: form.pct_affaires, portion_foyer: form.portion_foyer, repartition_lcu: form.repartition_lcu, repartition_ela: form.repartition_ela });

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, pct_affaires: normalizePctAffaires(form.pct_affaires), repartition_lcu: Number(form.repartition_lcu || 0), repartition_ela: Number(form.repartition_ela || 0), portion_foyer: Number(form.portion_foyer || 1), montant_defaut: Number(form.montant_defaut || 0), recurrent: form.recurrent ? 1 : 0 };
      if (existing) await api.put(`/depenses-perso/types/${existing.id}`, payload);
      else await api.post('/depenses-perso/types', payload);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={existing ? 'Modifier un type' : 'Nouveau type de dépense'} onClose={onClose}>
      <form className="dp-form" onSubmit={submit}>
        <div className="dp-form-grid two-cols">
          <div className="dp-field span-2"><label>Nom</label><input type="text" value={form.nom} onChange={(e) => patch('nom', e.target.value)} required /></div>
          <div className="dp-field"><label>Catégorie</label><select value={form.categorie} onChange={(e) => patch('categorie', e.target.value)}>{CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}</select></div>
          <div className="dp-field"><label>Montant habituel</label><input type="number" min="0" step="0.01" value={form.montant_defaut} onChange={(e) => patch('montant_defaut', e.target.value)} /></div>
          <div className="dp-field span-2"><label>Description</label><textarea rows="2" value={form.description} onChange={(e) => patch('description', e.target.value)} /></div>
        </div>
        <div className="dp-section-block">
          <div className="dp-section-block-title">Paramètres de déductibilité</div>
          <div className="dp-form-grid three-cols">
            <div className="dp-field"><label>% d'affaires</label><input type="number" min="0" step="0.0001" value={form.pct_affaires} onChange={(e) => patch('pct_affaires', e.target.value)} /></div>
            <div className="dp-field"><label>LCU</label><input type="number" min="0" step="0.01" value={form.repartition_lcu} onChange={(e) => patch('repartition_lcu', e.target.value)} /></div>
            <div className="dp-field"><label>ELA</label><input type="number" min="0" step="0.01" value={form.repartition_ela} onChange={(e) => patch('repartition_ela', e.target.value)} /></div>
          </div>
          <div className={`dp-inline-hint ${repartitionTotal === 100 ? 'success' : 'danger'}`}>Total répartition : {num(repartitionTotal, 0)}%</div>
          <div className="dp-field"><label>Portion foyer</label><input type="number" min="0" step="0.001" value={form.portion_foyer} onChange={(e) => patch('portion_foyer', e.target.value)} /></div>
          <div className="dp-toggle-row">
            <button type="button" className={`dp-toggle-chip${form.recurrent ? ' active' : ''}`} onClick={() => patch('recurrent', 1)}>Récurrent mensuel</button>
            <button type="button" className={`dp-toggle-chip${!form.recurrent ? ' active' : ''}`} onClick={() => patch('recurrent', 0)}>Ponctuel</button>
          </div>
        </div>
        <div className="dp-formula-box dp-formula-vertical">
          <strong>Exemple de calcul live</strong>
          <span>Pour 100$ payé :</span>
          <span>Déduction totale : 100 x {num(form.pct_affaires)}% x {num(form.portion_foyer, 3)} = {money(example.deduction_totale)}</span>
          <span>LCU : {money(example.deduction_lcu)} | ELA : {money(example.deduction_ela)}</span>
        </div>
        <div className="dp-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={saving || repartitionTotal !== 100}>{saving ? 'Sauvegarde...' : existing ? 'Sauvegarder' : 'Créer ce type'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalDepense({ types, existing, onClose, onSaved }) {
  const availableTypes = types.filter((type) => type.active && type.categorie !== 'kilometrage');
  const today = new Date().toISOString().slice(0, 10);
  const initialType = existing ? types.find((type) => type.id === existing.type_id) : availableTypes[0];
  const [selectedTypeId, setSelectedTypeId] = useState(existing?.type_id || initialType?.id || null);
  const [form, setForm] = useState({
    date_depense: existing?.date_depense || today,
    montant_brut: existing?.montant_brut ?? initialType?.montant_defaut ?? '',
    fournisseur: existing?.fournisseur || '',
    description: existing?.description || '',
    notes: existing?.notes || '',
    pct_affaires: pctAffairesInputValue(existing?.pct_affaires ?? initialType?.pct_affaires ?? 1),
    repartition_lcu: existing?.repartition_lcu ?? initialType?.repartition_lcu ?? 100,
    repartition_ela: existing?.repartition_ela ?? initialType?.repartition_ela ?? 0,
    portion_foyer: existing?.portion_foyer ?? initialType?.portion_foyer ?? 1,
    facture: null,
  });
  const [saving, setSaving] = useState(false);
  const selectedType = types.find((type) => type.id === Number(selectedTypeId));
  const patch = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!selectedType || existing) return;
    setForm((prev) => ({
      ...prev,
      montant_brut: prev.montant_brut || selectedType.montant_defaut || '',
      pct_affaires: pctAffairesInputValue(selectedType.pct_affaires),
      repartition_lcu: selectedType.repartition_lcu,
      repartition_ela: selectedType.repartition_ela,
      portion_foyer: selectedType.portion_foyer,
    }));
  }, [selectedType, existing]);

  const live = calcDeduction(form);

  async function submit(e) {
    e.preventDefault();
    if (!selectedTypeId) return;
    setSaving(true);
    try {
      const data = new FormData();
      data.append('type_id', selectedTypeId);
      data.append('date_depense', form.date_depense);
      data.append('montant_brut', Number(form.montant_brut || 0));
      data.append('pct_affaires', normalizePctAffaires(form.pct_affaires));
      data.append('repartition_lcu', Number(form.repartition_lcu || 0));
      data.append('repartition_ela', Number(form.repartition_ela || 0));
      data.append('portion_foyer', Number(form.portion_foyer || 1));
      data.append('fournisseur', form.fournisseur || '');
      data.append('description', form.description || '');
      data.append('notes', form.notes || '');
      if (form.facture) data.append('facture', form.facture);

      if (existing) await api.putForm(`/depenses-perso/items/${existing.id}`, data);
      else await api.postForm('/depenses-perso/items', data);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={existing ? 'Modifier une dépense' : 'Nouvelle dépense déductible'} onClose={onClose} wide>
      <form className="dp-form" onSubmit={submit}>
        <div className="dp-form-grid two-cols">
          <div className="dp-field span-2"><label>Type de dépense</label><select value={selectedTypeId || ''} onChange={(e) => setSelectedTypeId(Number(e.target.value))} required>{availableTypes.map((type) => <option key={type.id} value={type.id}>{type.nom}</option>)}</select></div>
          <div className="dp-field"><label>Date</label><DateInput value={form.date_depense} onChange={(v) => patch('date_depense', v)} required /></div>
          <div className="dp-field"><label>Montant payé</label><input type="number" min="0" step="0.01" value={form.montant_brut} onChange={(e) => patch('montant_brut', e.target.value)} required /></div>
          <div className="dp-field"><label>Fournisseur</label><input type="text" value={form.fournisseur} onChange={(e) => patch('fournisseur', e.target.value)} /></div>
          <div className="dp-field"><label>Portion foyer</label><input type="number" min="0" step="0.001" value={form.portion_foyer} onChange={(e) => patch('portion_foyer', e.target.value)} /></div>
        </div>
        <div className="dp-section-block">
          <div className="dp-section-block-title">Calcul déductibilité</div>
          <div className="dp-form-grid three-cols">
            <div className="dp-field"><label>% d'affaires</label><input type="number" min="0" step="0.0001" value={form.pct_affaires} onChange={(e) => patch('pct_affaires', e.target.value)} /></div>
            <div className="dp-field"><label>LCU</label><input type="number" min="0" step="0.01" value={form.repartition_lcu} onChange={(e) => patch('repartition_lcu', e.target.value)} /></div>
            <div className="dp-field"><label>ELA</label><input type="number" min="0" step="0.01" value={form.repartition_ela} onChange={(e) => patch('repartition_ela', e.target.value)} /></div>
          </div>
          <div className="dp-formula-box dp-formula-vertical">
            <strong>Déduction calculée live</strong>
            <span>Total déductible : {money(live.deduction_totale)}</span>
            <span>LCU : {money(live.deduction_lcu)}</span>
            <span>ELA : {money(live.deduction_ela)}</span>
          </div>
        </div>
        <div className="dp-form-grid two-cols">
          <div className="dp-field span-2"><label>Description</label><input type="text" value={form.description} onChange={(e) => patch('description', e.target.value)} /></div>
          <div className="dp-field span-2"><label>Notes</label><textarea rows="3" value={form.notes} onChange={(e) => patch('notes', e.target.value)} /></div>
          <div className="dp-field span-2">
            <label>Facture (PDF, JPG, PNG)</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => patch('facture', e.target.files?.[0] || null)} />
            {existing?.facture_url && !form.facture && (
              <a href={uploadUrl(existing.facture_url)} target="_blank" rel="noreferrer" className="dp-facture-link">
                <Icon name="facture" size={14} /> Voir la facture actuelle
              </a>
            )}
          </div>
        </div>
        <div className="dp-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function KilometrageSection({ associe, annee, config, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get(`/depenses-perso/deplacements?associe=${associe}&annee=${annee}`)); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [annee, associe]);

  useEffect(() => { load(); }, [load, refreshKey]);

  async function remove(id) {
    if (!window.confirm('Supprimer ce déplacement ?')) return;
    await api.delete(`/depenses-perso/deplacements/${id}`);
    load();
  }

  return (
    <div className="card dp-section-card rise-in">
      <div className="card-header">
        <div className="card-header-left">
          <span className="card-header-icon"><Icon name="transport" size={16} /></span>
          <span className="card-title">{associe} — {associe === 'LCU' ? 'Louis-Charles' : 'Étienne'}</span>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setModal(true); }}>
          <Icon name="ajouter" size={14} /> Ajouter
        </button>
      </div>
      <div className="dp-inline-stats">
        <span className="muted">Taux ARC</span>
        <span className="gold">{num(config?.taux_km || 0.72)}$/km</span>
        <span className="dp-stats-sep" />
        <span className="muted">KM total</span>
        <span>{num(data?.total_km, 1)}</span>
        <span className="dp-stats-sep" />
        <span className="muted">Déduction</span>
        <span className="gold">{money(data?.total_deduction)}</span>
      </div>
      {loading && (
        <div className="empty-state">
          <div className="skeleton" style={{ height: 18, width: 180 }} />
        </div>
      )}
      {!loading && data && (
        <div className="dp-table-wrap">
          <table className="esc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Destination</th>
                <th>Distance</th>
                <th>Déduction</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.deplacements.map((row) => (
                <tr key={row.id}>
                  <td>{row.date_dep}</td>
                  <td>{row.destination}</td>
                  <td>{num(row.distance_km, 1)} km</td>
                  <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{money(row.deduction)}</td>
                  <td className="dp-actions-cell">
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(row); setModal(true); }} title="Modifier">
                      <Icon name="modifier" size={14} />
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => remove(row.id)} title="Supprimer">
                      <Icon name="supprimer" size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {data.deplacements.length === 0 && (
                <tr><td colSpan="5" className="empty-state">Aucun déplacement pour {annee}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {modal && (
        <ModalDeplacement
          associe={associe}
          config={config}
          existing={editing}
          onClose={() => { setModal(false); setEditing(null); }}
          onSaved={() => { setModal(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function OngletVueEnsemble({ resume }) {
  const chartData = (resume?.depenses?.par_categorie || []).map((row) => ({
    categorie: CATEGORY_LABELS[row.categorie] || row.categorie,
    LCU: row.deduction_lcu,
    ELA: row.deduction_ela,
  }));

  return (
    <div className="dp-overview-grid">
      <div className="dp-overview-cards">
        <div className="card dp-summary-card rise-in">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="utilisateur" size={16} /></span>
              <span className="card-title">LCU — Louis-Charles</span>
            </div>
            <span className="badge" style={{ background: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--accent)' }}>LCU</span>
          </div>
          <div className="dp-summary-line"><span className="muted">Kilométrage</span><strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(resume?.kilometrage?.lcu?.deduction)}</strong></div>
          <div className="dp-summary-line"><span className="muted">Dépenses</span><strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(resume?.depenses?.deduction_lcu)}</strong></div>
          <div className="dp-summary-line dp-summary-total">
            <span>Total déductions</span>
            <strong className="gold" style={{ fontFeatureSettings: "'tnum' 1", fontFamily: "'Cinzel', serif" }}>{money(resume?.total_deductions?.lcu)}</strong>
          </div>
        </div>

        <div className="card dp-summary-card dp-summary-card--ela rise-in">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="utilisateur" size={16} /></span>
              <span className="card-title">ELA — Étienne</span>
            </div>
            <span className="badge" style={{ background: 'color-mix(in srgb, var(--info) 15%, transparent)', color: 'var(--info)' }}>ELA</span>
          </div>
          <div className="dp-summary-line"><span className="muted">Kilométrage</span><strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(resume?.kilometrage?.ela?.deduction)}</strong></div>
          <div className="dp-summary-line"><span className="muted">Dépenses</span><strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(resume?.depenses?.deduction_ela)}</strong></div>
          <div className="dp-summary-line dp-summary-total">
            <span>Total déductions</span>
            <strong className="gold" style={{ fontFeatureSettings: "'tnum' 1", fontFamily: "'Cinzel', serif" }}>{money(resume?.total_deductions?.ela)}</strong>
          </div>
        </div>
      </div>

      <div className="card dp-chart-card rise-in">
        <div className="card-header">
          <div className="card-header-left">
            <span className="card-header-icon"><Icon name="analytics" size={16} /></span>
            <span className="card-title">Déductions par catégorie</span>
          </div>
        </div>
        <p className="muted" style={{ fontSize: 12, marginBottom: 'var(--space-4)' }}>Répartition LCU et ELA pour l'année fiscale.</p>
        <div className="dp-chart-wrap">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis type="number" stroke="var(--muted)" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="categorie" stroke="var(--muted)" width={110} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value) => money(value)}
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow)' }}
                labelStyle={{ color: 'var(--text-soft)', fontSize: 11 }}
              />
              <Legend />
              <Bar dataKey="LCU" fill="var(--accent)" radius={[0, 6, 6, 0]} />
              <Bar dataKey="ELA" fill="var(--info)" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function OngletDepenses({ itemsData, types, onReload }) {
  const [filter, setFilter] = useState('all');
  const [shareFilter, setShareFilter] = useState('all');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const filteredItems = useMemo(() => (itemsData?.items || []).filter((item) => (filter === 'all' || item.categorie === filter) && (shareFilter === 'all' || shareOf(item) === shareFilter)), [filter, itemsData?.items, shareFilter]);

  async function remove(id) {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    await api.delete(`/depenses-perso/items/${id}`);
    onReload();
  }

  return (
    <div className="dp-section-stack">
      <div className="card dp-section-card rise-in">
        <div className="card-header">
          <div className="card-header-left">
            <span className="card-header-icon"><Icon name="recu" size={16} /></span>
            <span className="card-title">Dépenses déductibles</span>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setModal(true); }}>
            <Icon name="ajouter" size={14} /> Ajouter une dépense
          </button>
        </div>
        <div className="dp-inline-stats">
          <span className="muted">Brut</span>
          <span style={{ fontFeatureSettings: "'tnum' 1" }}>{money(itemsData?.totaux?.montant_brut)}</span>
          <span className="dp-stats-sep" />
          <span className="muted">Déd. LCU</span>
          <span className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{money(itemsData?.totaux?.deduction_lcu)}</span>
          <span className="dp-stats-sep" />
          <span className="muted">Déd. ELA</span>
          <span style={{ color: 'var(--info)', fontFeatureSettings: "'tnum' 1" }}>{money(itemsData?.totaux?.deduction_ela)}</span>
        </div>
        <div className="dp-filters-wrap">
          <div className="dp-chip-group">
            <button className={`dp-filter-chip${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>Tous</button>
            {CATEGORIES.filter((c) => c !== 'kilometrage').map((c) => (
              <button key={c} className={`dp-filter-chip${filter === c ? ' active' : ''}`} onClick={() => setFilter(c)}>{CATEGORY_LABELS[c]}</button>
            ))}
          </div>
          <div className="dp-chip-group">
            {['all', 'LCU', 'ELA', 'Partagée'].map((v) => (
              <button key={v} className={`dp-filter-chip${shareFilter === v ? ' active' : ''}`} onClick={() => setShareFilter(v)}>{v === 'all' ? 'Tous associés' : v}</button>
            ))}
          </div>
        </div>
        <div className="dp-table-wrap">
          <table className="esc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Fournisseur</th>
                <th>Montant brut</th>
                <th>% affaires</th>
                <th>Déd. LCU</th>
                <th>Déd. ELA</th>
                <th>Facture</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td>{MONTHS[monthOf(item)] || item.date_depense} {String(item.annee_fiscale).slice(-2)}</td>
                  <td>
                    <div>{item.type_nom}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{CATEGORY_LABELS[item.categorie] || item.categorie}</div>
                  </td>
                  <td>{item.fournisseur || '—'}</td>
                  <td style={{ fontFeatureSettings: "'tnum' 1" }}>{money(item.montant_brut)}</td>
                  <td>{pctAffairesDisplay(item.pct_affaires)}</td>
                  <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{money(item.deduction_lcu)}</td>
                  <td style={{ color: 'var(--info)', fontFeatureSettings: "'tnum' 1" }}>{money(item.deduction_ela)}</td>
                  <td>
                    {item.facture_url ? (
                      <a href={uploadUrl(item.facture_url)} target="_blank" rel="noreferrer" className="dp-facture-link" title="Voir la facture">
                        <Icon name="facture" size={15} />
                      </a>
                    ) : '—'}
                  </td>
                  <td className="dp-actions-cell">
                    {item._source === 'depenses_autres' ? (
                      <span className="muted" style={{ fontSize: 11 }}>auto</span>
                    ) : (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(item); setModal(true); }} title="Modifier"><Icon name="modifier" size={14} /></button>
                        <button className="btn btn-danger btn-sm" onClick={() => remove(item.id)} title="Supprimer"><Icon name="supprimer" size={14} /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {filteredItems.length === 0 && (
                <tr><td colSpan="9" className="empty-state">Aucune dépense pour ce filtre.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {modal && (
        <ModalDepense
          types={types}
          existing={editing}
          onClose={() => { setModal(false); setEditing(null); }}
          onSaved={() => { setModal(false); setEditing(null); onReload(); }}
        />
      )}
    </div>
  );
}

function OngletTypes({ types, onReload }) {
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const groups = useMemo(() => {
    const map = {};
    for (const type of types) {
      if (!map[type.categorie]) map[type.categorie] = [];
      map[type.categorie].push(type);
    }
    return map;
  }, [types]);

  async function archive(type) {
    if (!window.confirm(`Archiver "${type.nom}" ?`)) return;
    await api.patch(`/depenses-perso/types/${type.id}/archiver`, {});
    onReload();
  }

  return (
    <div className="dp-section-stack">
      {Object.entries(groups).map(([categorie, categoryTypes]) => (
        <div key={categorie} className="card dp-section-card rise-in">
          <div className="card-header">
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="liste" size={16} /></span>
              <span className="card-title">{CATEGORY_LABELS[categorie] || categorie}</span>
            </div>
            <button className="btn btn-ghost" onClick={() => { setEditing(null); setModal(true); }}>
              <Icon name="ajouter" size={14} /> Nouveau type
            </button>
          </div>
          <p className="muted" style={{ fontSize: 12, marginBottom: 'var(--space-5)' }}>Types configurables sans toucher au code.</p>
          <div className="dp-types-grid">
            {categoryTypes.map((type) => (
              <div key={type.id} className={`dp-type-card${type.active ? '' : ' archived'}`}>
                <div className="dp-type-card-title">{type.nom}</div>
                <div className="dp-type-card-meta">{pctAffairesDisplay(type.pct_affaires)} affaires x {num(type.portion_foyer, 3)}</div>
                <div className="dp-type-card-meta">LCU {num(type.repartition_lcu, 0)}% / ELA {num(type.repartition_ela, 0)}%</div>
                <div className="dp-type-card-meta">{type.recurrent ? 'Récurrent' : 'Ponctuel'}</div>
                {type.montant_defaut > 0 && <div className="dp-type-card-meta">Montant suggéré : {money(type.montant_defaut)}</div>}
                {!type.active && <span className="badge" style={{ color: 'var(--muted)', fontSize: 11 }}>Archivé</span>}
                <div className="dp-type-card-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(type); setModal(true); }}>Modifier</button>
                  {type.active && <button className="btn btn-danger btn-sm" onClick={() => archive(type)}>Archiver</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {modal && (
        <ModalTypeDepense
          existing={editing}
          onClose={() => { setModal(false); setEditing(null); }}
          onSaved={() => { setModal(false); setEditing(null); onReload(); }}
        />
      )}
    </div>
  );
}

function OngletResume({ annee, resume }) {
  const [exportLoading, setExportLoading] = useState(null);

  function exportExcel() {
    const wb = XLSX.utils.book_new();
    const rows = [[`DEDUCTIONS FY${annee} - ESCAMEL`], [], ['LCU - Louis-Charles Cuierrier'], ['Kilometrage', money(resume?.kilometrage?.lcu?.deduction)]];
    (resume?.depenses?.par_type || []).forEach((row) => { if (row.deduction_lcu > 0) rows.push([row.nom, money(row.deduction_lcu)]); });
    (resume?.autres?.LCU || []).forEach((row) => rows.push([row.description, money(row.deduction)]));
    rows.push(['TOTAL DEDUCTIONS LCU', money(resume?.total_deductions?.lcu)], [], ['ELA - Etienne Lapointe'], ['Kilometrage', money(resume?.kilometrage?.ela?.deduction)]);
    (resume?.depenses?.par_type || []).forEach((row) => { if (row.deduction_ela > 0) rows.push([row.nom, money(row.deduction_ela)]); });
    (resume?.autres?.ELA || []).forEach((row) => rows.push([row.description, money(row.deduction)]));
    rows.push(['TOTAL DEDUCTIONS ELA', money(resume?.total_deductions?.ela)], [], ['TOTAL GLOBAL', money(resume?.total_deductions?.global)]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 48 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws, `FY${annee}`);
    XLSX.writeFile(wb, `Escamel_Deductions_FY${annee}.xlsx`);
  }

  async function exportFicheFiscale(associe) {
    setExportLoading(associe);
    try {
      const response = await fetch(`/api/etats/export-fiche/${associe}/${annee}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('escamel_token')}` },
      });
      if (!response.ok) throw new Error(`Erreur ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Escamel_FicheFiscale_${associe}_FY${annee}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Erreur export PDF : ${e.message}`);
    } finally {
      setExportLoading(null);
    }
  }

  return (
    <div className="dp-section-stack">
      <div className="card dp-section-card rise-in">
        <div className="card-header">
          <div className="card-header-left">
            <span className="card-header-icon"><Icon name="document" size={16} /></span>
            <span className="card-title">Rapport complet FY{annee}</span>
          </div>
          <div className="dp-resume-actions">
            <button className="btn btn-primary" onClick={exportExcel}>
              <Icon name="telecharger" size={14} /> Exporter Excel
            </button>
            <button className="btn btn-ghost" onClick={() => exportFicheFiscale('LCU')} disabled={exportLoading === 'LCU'}>
              <Icon name="document" size={14} /> {exportLoading === 'LCU' ? 'Generation...' : 'Fiche fiscale LCU'}
            </button>
            <button className="btn btn-ghost" onClick={() => exportFicheFiscale('ELA')} disabled={exportLoading === 'ELA'}>
              <Icon name="document" size={14} /> {exportLoading === 'ELA' ? 'Generation...' : 'Fiche fiscale ELA'}
            </button>
          </div>
        </div>
        <div className="dp-report-card">
          <div className="dp-report-title">DEDUCTIONS FY{annee} — ESCAMEL</div>
          <div className="dp-report-section">
            <h4 className="dp-report-assoc">LCU — Louis-Charles Cuierrier</h4>
            <div className="dp-report-line">
              <span>Kilométrage ({num(resume?.kilometrage?.lcu?.km, 1)} km x {num(resume?.kilometrage?.lcu?.taux_km)}$)</span>
              <strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(resume?.kilometrage?.lcu?.deduction)}</strong>
            </div>
            {(resume?.depenses?.par_type || []).filter((row) => row.deduction_lcu > 0).map((row) => (
              <div key={`lcu-${row.id}`} className="dp-report-line">
                <span>{row.nom}</span>
                <strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(row.deduction_lcu)}</strong>
              </div>
            ))}
            {(resume?.autres?.LCU || []).map((row) => (
              <div key={`lcu-autre-${row.id}`} className="dp-report-line">
                <span>{row.description}</span>
                <strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(row.deduction)}</strong>
              </div>
            ))}
            <div className="dp-report-total">
              <span>TOTAL DEDUCTIONS LCU</span>
              <strong className="gold" style={{ fontFeatureSettings: "'tnum' 1", fontFamily: "'Cinzel', serif" }}>{money(resume?.total_deductions?.lcu)}</strong>
            </div>
          </div>
          <div className="dp-report-section">
            <h4 className="dp-report-assoc">ELA — Étienne Lapointe</h4>
            <div className="dp-report-line">
              <span>Kilométrage ({num(resume?.kilometrage?.ela?.km, 1)} km x {num(resume?.kilometrage?.ela?.taux_km)}$)</span>
              <strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(resume?.kilometrage?.ela?.deduction)}</strong>
            </div>
            {(resume?.depenses?.par_type || []).filter((row) => row.deduction_ela > 0).map((row) => (
              <div key={`ela-${row.id}`} className="dp-report-line">
                <span>{row.nom}</span>
                <strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(row.deduction_ela)}</strong>
              </div>
            ))}
            {(resume?.autres?.ELA || []).map((row) => (
              <div key={`ela-autre-${row.id}`} className="dp-report-line">
                <span>{row.description}</span>
                <strong style={{ fontFeatureSettings: "'tnum' 1" }}>{money(row.deduction)}</strong>
              </div>
            ))}
            <div className="dp-report-total">
              <span>TOTAL DEDUCTIONS ELA</span>
              <strong className="gold" style={{ fontFeatureSettings: "'tnum' 1", fontFamily: "'Cinzel', serif" }}>{money(resume?.total_deductions?.ela)}</strong>
            </div>
          </div>
          <div className="dp-report-grand-total">
            <span>TOTAL GLOBAL</span>
            <strong className="gold" style={{ fontFeatureSettings: "'tnum' 1", fontFamily: "'Cinzel', serif", fontSize: 20 }}>{money(resume?.total_deductions?.global)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SKELETON ─────────────────────────────────────────────────────────────────
function DepensesPersoSkeleton() {
  return (
    <div className="dp-page">
      <div className="dp-header">
        <div>
          <div className="skeleton" style={{ height: 34, width: 340, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 12, width: 220 }} />
        </div>
        <div className="skeleton" style={{ height: 36, width: 120, borderRadius: 'var(--radius-md)' }} />
      </div>
      <div className="dp-onglets-bar">
        {ONGLETS.map((t) => <div key={t.id} className="skeleton" style={{ height: 34, width: 100, borderRadius: 999 }} />)}
      </div>
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        <div className="skeleton" style={{ height: 18, width: 200, marginBottom: 'var(--space-5)' }} />
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton" style={{ height: 14, width: '100%', marginBottom: 'var(--space-3)' }} />
        ))}
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────────
export default function DepensesPerso() {
  const [onglet, setOnglet] = useState('overview');
  const [annee, setAnnee] = useState(CURRENT_YEAR);
  const [config, setConfig] = useState(null);
  const [types, setTypes] = useState([]);
  const [itemsData, setItemsData] = useState({ items: [], totaux: {}, par_categorie: [] });
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [configData, typesData, items, summary] = await Promise.all([
        api.get('/depenses-perso/config'),
        api.get('/depenses-perso/types?inclure_archives=1'),
        api.get(`/depenses-perso/items?annee=${annee}`),
        api.get(`/depenses-perso/resume/${annee}`),
      ]);
      setConfig(configData);
      setTypes(typesData);
      setItemsData(items);
      setResume(summary);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [annee]);

  useEffect(() => { loadAll(); }, [loadAll, refreshIndex]);
  const triggerReload = () => setRefreshIndex((v) => v + 1);
  const subtitle = useMemo(() => `Dépenses personnelles déductibles — Taux ARC ${num(config?.LCU?.taux_km || 0.72)}$/km`, [config?.LCU?.taux_km]);

  if (loading) return <DepensesPersoSkeleton />;

  return (
    <div className="dp-page">
      <div className="dp-header">
        <div className="dp-header-text">
          <h1 className="page-title">Dépenses personnelles</h1>
          <p className="page-subtitle muted">{subtitle}</p>
        </div>
        <div className="dp-annee-selector">
          <label className="muted">Année fiscale</label>
          <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
            {[2025, 2026, 2027].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className="dp-onglets-bar">
        {ONGLETS.map((tab) => (
          <button
            key={tab.id}
            className={`dp-onglet-btn${onglet === tab.id ? ' active' : ''}`}
            onClick={() => setOnglet(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {onglet === 'overview' && <OngletVueEnsemble resume={resume} />}
      {onglet === 'km' && (
        <div className="dp-section-stack">
          <KilometrageSection associe="LCU" annee={annee} config={config?.LCU} refreshKey={refreshIndex} />
          <KilometrageSection associe="ELA" annee={annee} config={config?.ELA} refreshKey={refreshIndex} />
        </div>
      )}
      {onglet === 'depenses' && <OngletDepenses itemsData={itemsData} types={types} onReload={triggerReload} />}
      {onglet === 'types' && <OngletTypes types={types} onReload={triggerReload} />}
      {onglet === 'resume' && <OngletResume annee={annee} resume={resume} />}
    </div>
  );
}
